/**
 * Watrly Toast Component
 * ======================
 * مكون الرسائل العائمة (إشعارات أيفون / قطرة ماء)
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './Toast.css';

const Toast = ({
  id,
  type = 'info', // success | error | warning | info
  title,
  message,
  duration = 5000,
  position = 'top-center', // top-left | top-center | top-right | bottom-left | bottom-center | bottom-right
  onClose,
  action,
  icon,
  showProgress = true,
  variant = 'water-drop', // water-drop | default | minimal
}) => {
  const { isRTL } = useTheme();
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (duration && duration > 0) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);

        if (remaining <= 0) {
          handleClose();
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose?.(id);
    }, 300);
  };

  const getIcon = () => {
    if (icon) return icon;

    const icons = {
      success: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      error: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      ),
      warning: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
      info: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      ),
    };

    return icons[type] || icons.info;
  };

  const classes = [
    'watrly-toast',
    `watrly-toast--${type}`,
    `watrly-toast--${variant}`,
    isExiting && 'watrly-toast--exiting',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} dir={isRTL ? 'rtl' : 'ltr'} role="alert">
      {variant === 'water-drop' && (
        <div className="watrly-toast__drop-effect" />
      )}

      <div className="watrly-toast__icon">
        {getIcon()}
      </div>

      <div className="watrly-toast__content">
        {title && <h4 className="watrly-toast__title">{title}</h4>}
        {message && <p className="watrly-toast__message">{message}</p>}
        {action && (
          <button
            className="watrly-toast__action"
            onClick={() => {
              action.onClick?.();
              handleClose();
            }}
          >
            {action.label}
          </button>
        )}
      </div>

      <button
        className="watrly-toast__close"
        onClick={handleClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {showProgress && duration > 0 && (
        <div className="watrly-toast__progress">
          <div
            className="watrly-toast__progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// Toast Container - حاوية الرسائل
export const ToastContainer = ({ toasts = [], position = 'top-center', onRemove }) => {
  const positionClasses = {
    'top-left': 'watrly-toast-container--top-left',
    'top-center': 'watrly-toast-container--top-center',
    'top-right': 'watrly-toast-container--top-right',
    'bottom-left': 'watrly-toast-container--bottom-left',
    'bottom-center': 'watrly-toast-container--bottom-center',
    'bottom-right': 'watrly-toast-container--bottom-right',
  };

  return (
    <div className={`watrly-toast-container ${positionClasses[position] || positionClasses['top-center']}`}>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={onRemove}
        />
      ))}
    </div>
  );
};

// Toast Hook - خطاف استخدام الرسائل
let toastId = 0;
let addToastFn = null;

export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (options) => {
    const id = ++toastId;
    const toast = { id, ...options };
    setToasts((prev) => [...prev, toast]);
    return id;
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const toast = {
    success: (message, options = {}) => addToast({ type: 'success', message, ...options }),
    error: (message, options = {}) => addToast({ type: 'error', message, ...options }),
    warning: (message, options = {}) => addToast({ type: 'warning', message, ...options }),
    info: (message, options = {}) => addToast({ type: 'info', message, ...options }),
    custom: (options) => addToast(options),
  };

  addToastFn = addToast;

  return { toasts, toast, removeToast };
};

// Global toast function
export const toast = {
  success: (message, options = {}) => addToastFn?.({ type: 'success', message, ...options }),
  error: (message, options = {}) => addToastFn?.({ type: 'error', message, ...options }),
  warning: (message, options = {}) => addToastFn?.({ type: 'warning', message, ...options }),
  info: (message, options = {}) => addToastFn?.({ type: 'info', message, ...options }),
};

export default Toast;
